import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import {
  attachAgentSessionToBody,
  buildAgentSessionRequestConfig,
} from "./agentApiSession";

const ACCOUNT_OVERVIEW_PATH = "/api/agent/account-overview";
const TEAM_AGENCY_NAME_PATH = "/api/agent/team/agency-name";
const BILLING_EXEMPTION_SALES_AUDIT_PATH = "/api/agent/billing-exemption/sales-audit";

function accountOverviewUrl() {
  return resolveApiUrl(ACCOUNT_OVERVIEW_PATH);
}

/**
 * סיכום חשבון סוכן (מנוי, חיובים, צוות) — מקור שרת.
 * @param {{ skipBillingExemption?: boolean }} [options]
 * @returns {Promise<object>}
 */
export async function getAgentAccountOverview({
  skipBillingExemption = false,
} = {}) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 45000 });
  const { data } = await axios.post(
    accountOverviewUrl(),
    attachAgentSessionToBody({
      sessionID: sid,
      skip_billing_exemption: Boolean(skipBillingExemption),
    }),
    { headers, withCredentials, timeout },
  );
  return data;
}

/**
 * מוביל צוות מעדכן שם סוכנות שמוצג בדשבורד של כל חברי הצוות.
 * @param {string} agencyName
 */
export async function updateTeamAgencyName(agencyName) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 20000 });
  const { data } = await axios.put(
    resolveApiUrl(TEAM_AGENCY_NAME_PATH),
    attachAgentSessionToBody({
      sessionID: sid,
      agencyName: agencyName == null ? "" : String(agencyName),
    }),
    { headers, withCredentials, timeout },
  );
  return data;
}

/**
 * שם סוכנות לתצוגה בדשבורד (מוביל או חבר צוות).
 * משתמש ב-POST+sessionID כמו account-overview (אמין יותר מ-GET בסשן אדמין).
 * @returns {Promise<{ ok?: boolean, agencyName?: string|null, isTeamLead?: boolean }>}
 */
export async function fetchTeamAgencyName() {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 45000 });
  const { data } = await axios.post(
    resolveApiUrl("/api/agent/team/agency-display"),
    attachAgentSessionToBody({ sessionID: sid }),
    { headers, withCredentials, timeout },
  );
  return data;
}

export async function getAgentBillingExemptionSalesAudit(month) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 25000 });
  const m = String(month || "").trim();
  if (!m) {
    throw new Error("sales_audit_month_required");
  }
  const { data } = await axios.get(
    resolveApiUrl(`${BILLING_EXEMPTION_SALES_AUDIT_PATH}?month=${encodeURIComponent(m)}`),
    {
      headers,
      params: { month: m, sessionID: sid },
      withCredentials,
      timeout,
    },
  );
  return data;
}

/**
 * מוביל צוות מקים יוזר חדש תחת הצוות (מושב בחבילה).
 * @param {{ firstName: string, lastName: string, email: string, phone?: string, password: string }} body
 */
export async function createTeamSeatUser(body) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 20000 });
  const { data } = await axios.post(
    resolveApiUrl("/api/agent/team/users"),
    attachAgentSessionToBody({ sessionID: sid, ...body }),
    { headers, withCredentials, timeout },
  );
  return data;
}

/**
 * חלוקת דמי טיפול ברירת מחדל לצוות — אחוז שנשאר אצל הסוכן.
 * @param {number} agentPercent
 */
export async function updateTeamHandlingFeeSplit(agentPercent) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 20000 });
  const { data } = await axios.put(
    resolveApiUrl("/api/agent/team/handling-fee-split"),
    attachAgentSessionToBody({
      sessionID: sid,
      agentPercent,
    }),
    { headers, withCredentials, timeout },
  );
  return data;
}

/**
 * חלוקת דמי טיפול לסוכן ספציפי בצוות.
 * @param {number} memberId
 * @param {number} agentPercent
 */
export async function updateMemberHandlingFeeSplit(memberId, agentPercent) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 20000 });
  const { data } = await axios.put(
    resolveApiUrl(`/api/agent/team/members/${memberId}/handling-fee-split`),
    attachAgentSessionToBody({
      sessionID: sid,
      agentPercent,
    }),
    { headers, withCredentials, timeout },
  );
  return data;
}

const HOME_SUMMARY_PATH = "/api/agent/home-summary";

/**
 * Totals-only wallet KPIs for /p/dashboard/home (replaces 4 legacy commission POSTs on home).
 * @returns {Promise<{
 *   futureTotal: number,
 *   availableTotal: number,
 *   historyTotal: number,
 *   withdrawableTotal: number,
 *   possibleProfit: number,
 *   calculationVersion?: string,
 *   availableSource?: string,
 * }>}
 */
export async function getAgentHomeSummary() {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 45000 });
  const { data } = await axios.post(
    resolveApiUrl(HOME_SUMMARY_PATH),
    attachAgentSessionToBody({ sessionID: sid }),
    { headers, withCredentials, timeout },
  );
  return data;
}

const HOME_RECENT_ORDERS_PATH = "/api/agent/home-recent-orders";

/**
 * Slim recent-orders feed for /p/dashboard/home (Phase 2).
 * @param {{ limit?: number }} [opts]
 * @returns {Promise<{ success?: boolean, orders?: array, limit?: number }>}
 */
export async function getAgentHomeRecentOrders({ limit = 15 } = {}) {
  const { sid, headers, withCredentials, timeout } =
    await buildAgentSessionRequestConfig({ timeout: 45000 });
  const { data } = await axios.post(
    resolveApiUrl(HOME_RECENT_ORDERS_PATH),
    attachAgentSessionToBody({
      sessionID: sid,
      limit,
      home: true,
    }),
    { headers, withCredentials, timeout },
  );
  return data;
}
